import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title1,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
  Field,
  RadioGroup,
  Radio,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldPersonRegular,
  EditRegular,
  SaveRegular,
  DismissRegular,
  SelectAllOnRegular,
  EyeOffRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import DataTable, { type Column } from '../components/DataTable';
import { getSystemUsers, getStaffRoles, upsertStaffRole } from '../services/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useStaffRolesScopeParams } from './useStaffRolesScopeParams';
import {
  computeBulkRoleUpdate,
  type BulkRoleMode,
  type BulkReviewerChange,
} from './staffRoleBulkEdit';
import type { StaffRole, StaffRoleType } from '../types';

// Dropdown sentinel — "don't touch the reviewer mapping" in the bulk dialog.
const KEEP_REVIEWER = '__keep__';

// Module-level, not inline in JSX: an inline array literal is a new reference every render, which
// (like the unmemoized `columns` this file also fixes) feeds DataTable's processed-data memo and
// retriggers the onPageDataChange effect every render -- the other half of the render-loop hang
// (PTCFinancial/ptc-core#386). TaxReturnsPage.tsx's own `SEARCH_KEYS` constant is the existing,
// proven pattern this mirrors.
const SEARCH_KEYS: (keyof MergedUser)[] = ['fullName', 'email'];

interface MergedUser {
  systemUserId: string;
  fullName: string;
  email?: string;
  jobTitle?: string;
  roles: StaffRoleType[];
  reviewerId?: string;
  isActive: boolean;
  hasRoleRecord: boolean;
}

const useBulkStyles = makeStyles({
  bulkBar: {
    position: 'sticky',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 24px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '8px 8px 0 0',
    boxShadow: tokens.shadow16,
    zIndex: 100,
  },
  bulkCount: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    minWidth: '120px',
  },
  bulkMessage: {
    padding: '12px 16px',
    marginBottom: '16px',
    borderRadius: '4px',
  },
});

type StatusFilter = 'active' | 'inactive' | 'all';
type RoleFilter = StaffRoleType | 'all' | 'none';

export default function StaffRolesPage() {
  const { t } = useTranslation();
  const { isAdmin } = useCurrentUser();
  const queryClient = useQueryClient();
  const bulkStyles = useBulkStyles();

  // Role display labels — role VALUES ('admin', 'preparer', etc.) stay in English as the
  // literal strings sent to the backend; only the displayed label is translated.
  const ALL_ROLES: { value: StaffRoleType; label: string; color: 'brand' | 'danger' | 'important' | 'informative' | 'subtle' }[] = useMemo(() => [
    { value: 'admin', label: t('staffRolesPage.roleLabels.admin'), color: 'danger' },
    { value: 'preparer', label: t('staffRolesPage.roleLabels.preparer'), color: 'brand' },
    { value: 'reviewer', label: t('staffRolesPage.roleLabels.reviewer'), color: 'important' },
    { value: 'reception', label: t('staffRolesPage.roleLabels.reception'), color: 'informative' },
    { value: 'staff', label: t('staffRolesPage.roleLabels.staff'), color: 'subtle' },
  ], [t]);

  const [editingUser, setEditingUser] = useState<MergedUser | null>(null);
  const [editRoles, setEditRoles] = useState<StaffRoleType[]>([]);
  const [editReviewerId, setEditReviewerId] = useState<string>('');
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);

  // Filter state
  const { statusFilter, roleFilter, setStatusFilter, setRoleFilter } = useStaffRolesScopeParams();

  // Admin removal confirmation
  const [confirmRemoveAdmin, setConfirmRemoveAdmin] = useState<{ type: 'single'; names: string[] } | { type: 'bulk'; names: string[] } | null>(null);

  // Multi-select state (admin only)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pageItems, setPageItems] = useState<MergedUser[]>([]);
  const [bulkRoles, setBulkRoles] = useState<StaffRoleType[]>([]);
  const [bulkMode, setBulkMode] = useState<BulkRoleMode>('add');
  const [bulkReviewerId, setBulkReviewerId] = useState<string>(KEEP_REVIEWER);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  // No `= []` default here (PTCFinancial/ptc-core#386): a destructuring default re-evaluates to a
  // BRAND NEW array every render for as long as `data` is undefined (the whole loading phase), so
  // `systemUsers`/`staffRoles` were never reference-stable while loading -- that instability
  // cascaded through mergedUsers -> filteredUsers -> DataTable's data prop -> its processed-data
  // memo -> the onPageDataChange effect, which calls setPageItems on every single render, which
  // retriggers a render, forever (a true infinite loop, confirmed by mechanical JSX bisection: it
  // clears the instant onPageDataChange is removed). `undefined` is trivially reference-stable
  // across renders (there is only one), so leaving `data` as `undefined` and falling back to `[]`
  // INSIDE the already-memoized `mergedUsers` below (mirrors TaxReturnsPage.tsx's own
  // `(taxReturns || []).filter(...)` inside ITS memo) keeps every render's reference stable until
  // the query actually resolves.
  const { data: systemUsers, isLoading: loadingUsers, isError: errorUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
  });

  const { data: staffRoles, isLoading: loadingRoles, isError: errorRoles, refetch: refetchRoles } = useQuery({
    queryKey: ['staffRoles'],
    queryFn: getStaffRoles,
  });

  const upsertMutation = useMutation({
    mutationFn: (role: StaffRole) => upsertStaffRole(role.systemUserId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffRoles'] });
      setEditingUser(null);
    },
    onError: (error: Error) => {
      setBulkMessage(t('staffRolesPage.messages.saveError', { message: error.message }));
      setTimeout(() => setBulkMessage(null), 5000);
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (params: {
      ids: string[];
      roles: StaffRoleType[];
      mode: BulkRoleMode;
      reviewer: BulkReviewerChange;
      markInactive: boolean;
    }) => {
      const promises = params.ids.map(id => {
        const user = mergedUsers.find(u => u.systemUserId === id);
        if (!user) return Promise.resolve(null);
        const next = computeBulkRoleUpdate(user, {
          roles: params.roles,
          mode: params.mode,
          reviewer: params.reviewer,
          markInactive: params.markInactive,
        });
        return upsertStaffRole(id, {
          id,
          systemUserId: id,
          fullName: user.fullName,
          email: user.email,
          roles: next.roles,
          reviewerId: next.reviewerId,
          isActive: next.isActive,
          updatedAt: new Date().toISOString(),
        });
      });
      return Promise.all(promises);
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['staffRoles'] });
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
      setBulkRoles([]);
      setBulkMode('add');
      setBulkReviewerId(KEEP_REVIEWER);
      setBulkMessage(
        params.markInactive
          ? t('staffRolesPage.messages.bulkSuccessMarkedInactive', { count: params.ids.length })
          : t('staffRolesPage.messages.bulkSuccessUpdated', { count: params.ids.length })
      );
      setTimeout(() => setBulkMessage(null), 5000);
    },
    onError: (error: Error) => {
      setBulkMessage(t('staffRolesPage.messages.bulkError', { message: error.message }));
      setTimeout(() => setBulkMessage(null), 5000);
    },
  });

  // Merge system users with their staff role records
  const mergedUsers = useMemo(() => {
    const roleMap = new Map((staffRoles ?? []).map(r => [r.systemUserId, r]));
    return (systemUsers ?? [])
      .filter(u => !u.isDisabled)
      .map((user): MergedUser => {
        const roleRecord = roleMap.get(user.systemUserId);
        return {
          systemUserId: user.systemUserId,
          fullName: user.fullName,
          email: user.email,
          jobTitle: user.jobTitle,
          roles: (roleRecord?.roles ?? []) as StaffRoleType[],
          reviewerId: roleRecord?.reviewerId,
          isActive: roleRecord?.isActive ?? true,
          hasRoleRecord: !!roleRecord,
        };
      });
  }, [systemUsers, staffRoles]);

  // Active staff who can act as reviewers (for the preparer→reviewer picker)
  const reviewerOptions = useMemo(
    () => mergedUsers
      .filter(u => u.isActive && u.roles.includes('reviewer'))
      .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [mergedUsers]
  );

  // systemUserId → full name, for rendering the assigned-reviewer column
  const nameById = useMemo(
    () => new Map(mergedUsers.map(u => [u.systemUserId, u.fullName])),
    [mergedUsers]
  );

  // Apply both filters together
  const filteredUsers = useMemo(() => {
    let filtered = mergedUsers;

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(u => u.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(u => !u.isActive);
    }

    // Role filter
    if (roleFilter === 'none') {
      filtered = filtered.filter(u => u.roles.length === 0);
    } else if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.roles.includes(roleFilter));
    }

    return filtered;
  }, [mergedUsers, statusFilter, roleFilter]);

  const handleEditClick = useCallback((user: MergedUser) => {
    setEditingUser(user);
    setEditRoles([...user.roles]);
    setEditReviewerId(user.reviewerId ?? '');
  }, []);

  const handleRoleToggle = (role: StaffRoleType) => {
    setEditRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleBulkRoleToggle = (role: StaffRoleType) => {
    setBulkRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const executeSave = () => {
    if (!editingUser) return;
    upsertMutation.mutate({
      id: editingUser.systemUserId,
      systemUserId: editingUser.systemUserId,
      fullName: editingUser.fullName,
      email: editingUser.email,
      roles: editRoles,
      // Reviewer mapping only applies to preparers — cleared if the role is removed
      reviewerId: editRoles.includes('preparer') ? (editReviewerId || undefined) : undefined,
      isActive: true,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleSave = () => {
    if (!editingUser) return;
    // Check if admin role is being removed
    if (editingUser.roles.includes('admin') && !editRoles.includes('admin')) {
      setConfirmRemoveAdmin({ type: 'single', names: [editingUser.fullName] });
      return;
    }
    executeSave();
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // PTCFinancial/ptc-core#386 root cause: pageItems is refreshed on every table render (via
  // onPageDataChange), so a toggleSelectAll that CLOSES OVER pageItems/selectedIds gets a new
  // identity every render -- and toggleSelectAll is one of the `columns` memo's own dependencies,
  // so that in turn gives `columns` a new identity every render too, which feeds DataTable's own
  // processed-data memo, which re-fires the onPageDataChange effect, which calls setPageItems
  // again, forever. TaxReturnsPage.tsx's own toggleSelectAll already named and fixed this exact
  // trap (its comment: "a pageItems-dependent callback in the columns memo would feed the
  // onPageDataChange -> setPageItems -> columns-rebuild loop") -- mirrored verbatim here via refs.
  const pageItemsRef = useRef(pageItems);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => { pageItemsRef.current = pageItems; }, [pageItems]);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  const toggleSelectAll = useCallback(() => {
    const items = pageItemsRef.current;
    if (!items.length) return;
    const allSelected = items.every(item => selectedIdsRef.current.has(item.systemUserId));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) items.forEach(item => next.delete(item.systemUserId));
      else items.forEach(item => next.add(item.systemUserId));
      return next;
    });
  }, []);

  // Reviewer-dropdown sentinel → typed change for computeBulkRoleUpdate.
  const bulkReviewerChange: BulkReviewerChange =
    bulkReviewerId === KEEP_REVIEWER ? { type: 'keep' }
      : bulkReviewerId === '' ? { type: 'clear' }
        : { type: 'set', reviewerId: bulkReviewerId };

  // The bulk edit does something only if roles are chosen or the reviewer is changing.
  const bulkCanApply = bulkRoles.length > 0 || bulkReviewerId !== KEEP_REVIEWER;

  const resetBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkRoles([]);
    setBulkMode('add');
    setBulkReviewerId(KEEP_REVIEWER);
  };

  const executeBulkAssign = () => {
    if (selectedIds.size === 0 || !bulkCanApply) return;
    bulkMutation.mutate({
      ids: Array.from(selectedIds),
      roles: bulkRoles,
      mode: bulkMode,
      reviewer: bulkReviewerChange,
      markInactive: false,
    });
  };

  const handleBulkAssign = () => {
    if (selectedIds.size === 0 || !bulkCanApply) return;
    // Admin can only be lost in Replace mode — Add never removes a role.
    if (bulkMode === 'replace' && bulkRoles.length > 0 && !bulkRoles.includes('admin')) {
      const affectedAdmins = mergedUsers
        .filter(u => selectedIds.has(u.systemUserId) && u.roles.includes('admin'))
        .map(u => u.fullName);
      if (affectedAdmins.length > 0) {
        setConfirmRemoveAdmin({ type: 'bulk', names: affectedAdmins });
        return;
      }
    }
    executeBulkAssign();
  };

  const handleMarkInactive = () => {
    if (selectedIds.size === 0) return;
    bulkMutation.mutate({
      ids: Array.from(selectedIds),
      roles: [],
      mode: 'add',
      reviewer: { type: 'keep' },
      markInactive: true,
    });
  };

  const isLoading = loadingUsers || loadingRoles;
  const isError = Boolean(errorUsers || errorRoles);

  const allPageSelected = pageItems.length > 0 && pageItems.every(item => selectedIds.has(item.systemUserId));
  const somePageSelected = pageItems.some(item => selectedIds.has(item.systemUserId));

  // Filter display labels
  const statusLabels: Record<StatusFilter, string> = useMemo(() => ({
    active: t('staffRolesPage.filters.status.active'),
    inactive: t('staffRolesPage.filters.status.inactive'),
    all: t('staffRolesPage.filters.status.all'),
  }), [t]);
  const roleLabels: Record<RoleFilter, string> = useMemo(() => ({
    all: t('staffRolesPage.filters.role.all'),
    none: t('staffRolesPage.filters.role.none'),
    admin: t('staffRolesPage.roleLabels.admin'),
    preparer: t('staffRolesPage.roleLabels.preparer'),
    reviewer: t('staffRolesPage.roleLabels.reviewer'),
    reception: t('staffRolesPage.roleLabels.reception'),
    staff: t('staffRolesPage.roleLabels.staff'),
  }), [t]);

  // Build columns — conditional on isAdmin for select/actions. MEMOIZED: an unmemoized array here
  // is a fresh reference every render, which cascades through DataTable's own columns-keyed memo
  // into its processed-data memo, which feeds the onPageDataChange effect below -- every render
  // then fires that effect, which calls setPageItems, which triggers another render, forever. This
  // is the exact hazard TaxReturnsPage.tsx's own `exportColumns`/columns comments already name and
  // guard against; this page's P6 polish pass (2026-10-29) was the first time anything rendered
  // this component in a test, and nothing had caught the missing guard until then. Root-caused via
  // mechanical JSX bisection on main's pre-P6 source (PTCFinancial/ptc-core#386) -- confirmed a true
  // infinite render loop, not a timing flake, by tracing paginatedData's reference identity through
  // DataTable -> useTableState on every render.
  const columns: Column<MergedUser>[] = useMemo(() => [
    ...(isAdmin ? [{
      key: '_select',
      header: (
        <Checkbox
          checked={allPageSelected ? true : somePageSelected ? 'mixed' : false}
          onChange={toggleSelectAll}
        />
      ) as unknown as string,
      sortable: false,
      render: (user: MergedUser) => (
        <Checkbox
          checked={selectedIds.has(user.systemUserId)}
          onChange={(e: React.ChangeEvent) => {
            e.stopPropagation();
            toggleSelect(user.systemUserId);
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    } as Column<MergedUser>] : []),
    {
      key: 'fullName',
      header: t('staffRolesPage.table.columns.name'),
      sortable: true,
      render: (user) => (
        <span style={{ fontWeight: 600, opacity: user.isActive ? 1 : 0.5 }}>
          {user.fullName}
        </span>
      ),
    },
    {
      key: 'email',
      header: t('staffRolesPage.table.columns.email'),
      sortable: true,
      render: (user) => <span style={{ opacity: user.isActive ? 1 : 0.5 }}>{user.email || '—'}</span>,
    },
    {
      key: 'jobTitle',
      header: t('staffRolesPage.table.columns.jobTitle'),
      sortable: true,
      render: (user) => <span style={{ opacity: user.isActive ? 1 : 0.5 }}>{user.jobTitle || '—'}</span>,
    },
    {
      key: 'roles',
      header: t('staffRolesPage.table.columns.roles'),
      render: (user) => {
        if (!user.isActive) {
          return <Badge appearance="filled" color="danger">{t('staffRolesPage.table.inactiveBadge')}</Badge>;
        }
        if (user.roles.length === 0) {
          return <span style={{ color: '#999' }}>{t('staffRolesPage.table.noRolesAssigned')}</span>;
        }
        return (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {user.roles.map(role => {
              const roleDef = ALL_ROLES.find(r => r.value === role);
              return (
                <Badge
                  key={role}
                  appearance="filled"
                  color={roleDef?.color ?? 'subtle'}
                >
                  {roleDef?.label ?? role}
                </Badge>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'reviewer',
      header: t('staffRolesPage.table.columns.reviewer'),
      render: (user) => {
        if (!user.isActive || !user.roles.includes('preparer')) {
          return <span style={{ color: '#999' }}>{'—'}</span>;
        }
        const reviewerName = user.reviewerId ? nameById.get(user.reviewerId) : undefined;
        return reviewerName
          ? <span>{reviewerName}</span>
          : <span style={{ color: '#b88600' }}>{t('staffRolesPage.table.reviewerNotSet')}</span>;
      },
    },
    ...(isAdmin ? [{
      key: 'actions',
      header: t('staffRolesPage.table.columns.actions'),
      width: '80px',
      render: (user: MergedUser) => (
        <Button
          appearance="subtle"
          icon={<EditRegular />}
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleEditClick(user);
          }}
        >
          {t('staffRolesPage.table.editButton')}
        </Button>
      ),
    } as Column<MergedUser>] : []),
  ], [isAdmin, t, ALL_ROLES, allPageSelected, somePageSelected, toggleSelectAll, selectedIds, toggleSelect, nameById, handleEditClick]);

  // Every hook for this component has now run (above this line) -- safe to bail out early for
  // the error state without violating the Rules of Hooks on the next (possibly non-error) render.
  if (isError) {
    return (
      <div className="error-container">
        <p>{t('staffRolesPage.errors.loadFailed')}</p>
        <Button onClick={() => { refetchUsers(); refetchRoles(); }}>{t('staffRolesPage.errors.retry')}</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <ShieldPersonRegular style={{ fontSize: tokens.fontSizeHero700 }} />
        <Title1>{t('staffRolesPage.title')}</Title1>
      </div>
      <p style={{ color: '#666', margin: '0 0 16px 0' }}>
        {isAdmin
          ? t('staffRolesPage.description.admin')
          : t('staffRolesPage.description.readOnly')}
      </p>

      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <Dropdown
          value={statusLabels[statusFilter]}
          selectedOptions={[statusFilter]}
          onOptionSelect={(_, data) => setStatusFilter((data.optionValue as StatusFilter) || 'active')}
          style={{ minWidth: '140px' }}
        >
          <Option value="active">{statusLabels.active}</Option>
          <Option value="inactive">{statusLabels.inactive}</Option>
          <Option value="all">{statusLabels.all}</Option>
        </Dropdown>

        <Dropdown
          value={roleLabels[roleFilter]}
          selectedOptions={[roleFilter]}
          onOptionSelect={(_, data) => setRoleFilter((data.optionValue as RoleFilter) || 'all')}
          style={{ minWidth: '160px' }}
        >
          <Option value="all">{roleLabels.all}</Option>
          <Option value="none">{roleLabels.none}</Option>
          {ALL_ROLES.map(role => (
            <Option key={role.value} value={role.value}>{role.label}</Option>
          ))}
        </Dropdown>

        {(statusFilter !== 'active' || roleFilter !== 'all') && (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => { setStatusFilter('active'); setRoleFilter('all'); }}
          >
            {t('staffRolesPage.filters.clearFilters')}
          </Button>
        )}
      </div>

      {bulkMessage && (
        <div className={bulkStyles.bulkMessage} style={{
          backgroundColor: bulkMessage.startsWith('Error') ? '#fde7e9' : '#dff6dd',
          color: bulkMessage.startsWith('Error') ? '#a4262c' : '#107c10',
        }}>
          {bulkMessage}
        </div>
      )}

      <DataTable
        data={filteredUsers}
        columns={columns}
        rowKey="systemUserId"
        loading={isLoading}
        searchable
        searchPlaceholder={t('staffRolesPage.table.searchPlaceholder')}
        searchKeys={SEARCH_KEYS}
        emptyMessage={t('staffRolesPage.table.emptyMessage')}
        paginated
        initialSort={{ column: 'fullName', direction: 'asc' }}
        onPageDataChange={setPageItems}
      />

      {/* Bulk action bar (admin only) */}
      {isAdmin && selectedIds.size > 0 && (
        <div className={bulkStyles.bulkBar}>
          <SelectAllOnRegular />
          <span className={bulkStyles.bulkCount}>{t('staffRolesPage.bulkBar.selectedCount', { count: selectedIds.size })}</span>
          <Button
            appearance="secondary"
            onClick={() => setBulkDialogOpen(true)}
          >
            {t('staffRolesPage.bulkBar.bulkEditButton')}
          </Button>
          <Button
            appearance="secondary"
            icon={<EyeOffRegular />}
            onClick={handleMarkInactive}
            disabled={bulkMutation.isPending}
          >
            {t('staffRolesPage.bulkBar.markInactiveButton')}
          </Button>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={() => setSelectedIds(new Set())}
          >
            {t('staffRolesPage.bulkBar.clearButton')}
          </Button>
        </div>
      )}

      {/* Edit Roles Dialog — single user (admin only) */}
      {isAdmin && (
        <Dialog open={!!editingUser} onOpenChange={(_, data) => { if (!data.open) setEditingUser(null); }}>
          <DialogSurface>
            <DialogTitle>{t('staffRolesPage.editDialog.title', { name: editingUser?.fullName ?? '' })}</DialogTitle>
            <DialogBody>
              <DialogContent>
                <p style={{ marginTop: 0 }}>
                  {t('staffRolesPage.editDialog.selectRolesPrompt')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {ALL_ROLES.map(role => (
                    <Checkbox
                      key={role.value}
                      checked={editRoles.includes(role.value)}
                      onChange={() => handleRoleToggle(role.value)}
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <Badge appearance="filled" color={role.color}>{role.label}</Badge>
                        </span>
                      }
                    />
                  ))}
                </div>
                {editRoles.includes('preparer') && (
                  <div style={{ marginTop: '20px' }}>
                    <Field
                      label={t('staffRolesPage.editDialog.reviewerFieldLabel')}
                      hint={t('staffRolesPage.editDialog.reviewerFieldHint')}
                    >
                      <Dropdown
                        placeholder={t('staffRolesPage.editDialog.reviewerPlaceholder')}
                        value={editReviewerId ? (reviewerOptions.find(r => r.systemUserId === editReviewerId)?.fullName ?? '') : ''}
                        selectedOptions={editReviewerId ? [editReviewerId] : ['']}
                        onOptionSelect={(_, data) => setEditReviewerId((data.optionValue as string) || '')}
                      >
                        <Option value="" text={t('staffRolesPage.common.noReviewerOption')}>{t('staffRolesPage.common.noReviewerOption')}</Option>
                        {reviewerOptions
                          .filter(r => r.systemUserId !== editingUser?.systemUserId)
                          .map(r => (
                            <Option key={r.systemUserId} value={r.systemUserId}>{r.fullName}</Option>
                          ))}
                      </Dropdown>
                    </Field>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  icon={<DismissRegular />}
                  onClick={() => setEditingUser(null)}
                >
                  {t('staffRolesPage.common.cancel')}
                </Button>
                <Button
                  appearance="primary"
                  icon={<SaveRegular />}
                  onClick={handleSave}
                  disabled={upsertMutation.isPending}
                >
                  {upsertMutation.isPending ? t('staffRolesPage.editDialog.savingButton') : t('staffRolesPage.editDialog.saveButton')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {/* Bulk Edit Dialog (admin only) */}
      {isAdmin && (
        <Dialog open={bulkDialogOpen} onOpenChange={(_, data) => { if (!data.open) resetBulkDialog(); }}>
          <DialogSurface>
            <DialogTitle>{t('staffRolesPage.bulkEditDialog.title', { count: selectedIds.size })}</DialogTitle>
            <DialogBody>
              <DialogContent>
                <p style={{ marginTop: 0, color: '#666' }}>
                  {t('staffRolesPage.bulkEditDialog.applyDescription', { count: selectedIds.size })}
                </p>

                <div style={{ fontWeight: 600, marginBottom: '8px' }}>{t('staffRolesPage.bulkEditDialog.rolesHeading')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {ALL_ROLES.map(role => (
                    <Checkbox
                      key={role.value}
                      checked={bulkRoles.includes(role.value)}
                      onChange={() => handleBulkRoleToggle(role.value)}
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <Badge appearance="filled" color={role.color}>{role.label}</Badge>
                        </span>
                      }
                    />
                  ))}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <RadioGroup
                    value={bulkMode}
                    onChange={(_, data) => setBulkMode(data.value as BulkRoleMode)}
                  >
                    <Radio value="add" label={t('staffRolesPage.bulkEditDialog.modeAdd')} />
                    <Radio value="replace" label={t('staffRolesPage.bulkEditDialog.modeReplace')} />
                  </RadioGroup>
                </div>
                {bulkRoles.length === 0 && (
                  <p style={{ color: '#888', fontSize: tokens.fontSizeBase200, margin: '4px 0 0 0' }}>
                    {t('staffRolesPage.bulkEditDialog.noRolesSelectedHint')}
                  </p>
                )}

                <div style={{ fontWeight: 600, margin: '20px 0 8px 0' }}>{t('staffRolesPage.bulkEditDialog.reviewerHeading')}</div>
                <Field hint={t('staffRolesPage.bulkEditDialog.reviewerFieldHint')}>
                  <Dropdown
                    value={
                      bulkReviewerId === KEEP_REVIEWER ? t('staffRolesPage.common.leaveReviewerUnchanged')
                        : bulkReviewerId === '' ? t('staffRolesPage.common.noReviewerOption')
                          : (reviewerOptions.find(r => r.systemUserId === bulkReviewerId)?.fullName ?? '')
                    }
                    selectedOptions={[bulkReviewerId]}
                    onOptionSelect={(_, data) => setBulkReviewerId((data.optionValue as string) ?? KEEP_REVIEWER)}
                  >
                    <Option value={KEEP_REVIEWER} text={t('staffRolesPage.common.leaveReviewerUnchanged')}>{t('staffRolesPage.common.leaveReviewerUnchanged')}</Option>
                    <Option value="" text={t('staffRolesPage.common.noReviewerOption')}>{t('staffRolesPage.common.noReviewerOption')}</Option>
                    {reviewerOptions.map(r => (
                      <Option key={r.systemUserId} value={r.systemUserId}>{r.fullName}</Option>
                    ))}
                  </Dropdown>
                </Field>
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  icon={<DismissRegular />}
                  onClick={resetBulkDialog}
                >
                  {t('staffRolesPage.common.cancel')}
                </Button>
                <Button
                  appearance="primary"
                  icon={<SaveRegular />}
                  onClick={handleBulkAssign}
                  disabled={bulkMutation.isPending || !bulkCanApply}
                >
                  {bulkMutation.isPending ? t('staffRolesPage.bulkEditDialog.applyingButton') : t('staffRolesPage.bulkEditDialog.applyButton', { count: selectedIds.size })}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {/* Confirm Admin Role Removal Dialog */}
      <Dialog open={!!confirmRemoveAdmin} onOpenChange={(_, data) => { if (!data.open) setConfirmRemoveAdmin(null); }}>
        <DialogSurface>
          <DialogTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <WarningRegular style={{ color: '#d13438' }} />
              {t('staffRolesPage.confirmRemoveAdminDialog.title')}
            </span>
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              <p style={{ marginTop: 0 }}>
                <Trans
                  i18nKey="staffRolesPage.confirmRemoveAdminDialog.body"
                  components={{ badge: <Badge appearance="filled" color="danger" /> }}
                />
              </p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {confirmRemoveAdmin?.names.map(name => (
                  <li key={name} style={{ fontWeight: 600 }}>{name}</li>
                ))}
              </ul>
              <p style={{ color: '#d13438', margin: '12px 0 0 0' }}>
                {t('staffRolesPage.confirmRemoveAdminDialog.warningText')}
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setConfirmRemoveAdmin(null)}
              >
                {t('staffRolesPage.common.cancel')}
              </Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: '#d13438' }}
                onClick={() => {
                  const action = confirmRemoveAdmin?.type;
                  setConfirmRemoveAdmin(null);
                  if (action === 'single') executeSave();
                  else if (action === 'bulk') executeBulkAssign();
                }}
              >
                {t('staffRolesPage.confirmRemoveAdminDialog.removeButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
